// Hello, stranger! 👋
// Just a heads-up: the data in this file comes from the monolith MarketingBanners builder,
// located at /admin/marketing_banners

import { useMemo } from 'react';

import { Box, Container, Heading } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { InteractiveMarquee } from '../../components/interactive-marquee/interactive-marquee';
import { colors, containerSize } from '../../config/styles';
import { MotionBox } from '../motion-box/motion-box';
import { NewsCard } from '../news-card/news-card';
import { NewsCardSkeleton } from '../news-card/news-card-skeleton';
import { INewsPost } from '../news-card/types';
import { useGetMarketingBannersQuery } from './graphql/get-marketing-banners-query';
import {
  getFieldImageAssetUrls,
  getFieldTextValue,
  getFieldVideoAssetUrl,
} from './utils';

const MARKETING_BANNER_KEYS = ['us_homepage_whats_new'];
const SKELETON_COUNT = 3;

export const WhatsNew = observer(function WhatsNew() {
  const { data } = useGetMarketingBannersQuery({
    variables: { keys: MARKETING_BANNER_KEYS },
  });

  const newsPosts = useMemo<INewsPost[]>(() => {
    const slides =
      data?.marketingBanners.flatMap((banner) => banner.slides) ?? [];

    return slides
      .map((slide): INewsPost | undefined => {
        const fieldValues = slide.fieldValues;
        const title = getFieldTextValue(fieldValues, 'title') ?? '';
        const href = getFieldTextValue(fieldValues, 'url') ?? '#';
        const videoAssetUrl = getFieldVideoAssetUrl(fieldValues, 'video');
        const imageAssetUrls = getFieldImageAssetUrls(fieldValues, 'cover');

        if (videoAssetUrl) {
          const newsPost: INewsPost = {
            id: slide.id,
            title,
            href,
            video: {
              url: videoAssetUrl,
              poster: imageAssetUrls?.x2 ?? '',
            },
          };

          return newsPost;
        } else if (imageAssetUrls) {
          const newsPost: INewsPost = {
            id: slide.id,
            title,
            href,
            coverImage: {
              url: imageAssetUrls.x1,
              url2x: imageAssetUrls.x2,
            },
          };

          return newsPost;
        }

        return undefined;
      })
      .filter((newsPost): newsPost is INewsPost => newsPost !== undefined);
  }, [data]);

  const hasNewsPosts = newsPosts.length > 0;

  return (
    <Box
      as="section"
      id="whats-new"
      bg={{ sm: colors.lightNavy }}
      py={{ base: '10', sm: '18' }}
    >
      <Container maxW={containerSize}>
        <MotionBox>
          <Heading
            as="h2"
            size="title-md"
            mt="0"
            mb={{ base: '4', sm: '10', md: '14' }}
            color={colors.darkNavyTypography}
          >
            What’s new
          </Heading>

          {hasNewsPosts ? (
            <Box mx={{ base: '-4', md: '0' }}>
              <InteractiveMarquee spacing={{ base: '2', md: '3' }} delay={0.5}>
                {newsPosts.map((newsPost) => (
                  <NewsCard key={newsPost.id} newsPost={newsPost} />
                ))}
              </InteractiveMarquee>
            </Box>
          ) : (
            <Box overflowX="hidden">
              <Box display="flex" gap={{ base: '2', md: '3' }}>
                {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <NewsCardSkeleton key={`skeleton-${index}`} />
                ))}
              </Box>
            </Box>
          )}
        </MotionBox>
      </Container>
    </Box>
  );
});
